import { useQuery, gql, type QueryHookOptions } from "@apollo/client";

const PROPERTY_LISTING_QUERY = gql`
  query PropertyListing($slug: String!) {
    propertyListing(slug: $slug) {
      id
      publicRemarks
      listingId
      bedroomsTotal
      bathroomTotal
      parking
      parkingType
      moreInformationLink
      lease
      leasePerUnit
      leasePerTime
      price
      addressLine1
      city
      province
      postalCode
      unitNumber
      propertyType
      buildingType
      storiesTotal
      communityName
      ownershipType
      sizeTotalText
      constructedDate
      ammenitiesNearBy
      communityFeatures
      parkingSpaceTotal
      roadType
      basementDevelopment
      basementType
      halfBathTotal
      coolingType
      exteriorFinish
      flooringType
      heatingFuel
      heatingType
      constructionStyleAttachment
      sewer
      utilityWater
      images {
        id
        url
      }
      listingAgent {
        id
        name
        website
        position
        telephone
        teamMember {
          id
          slug
          name
          avatarThumb
        }
        office {
          id
          name
          addressLine1
          city
          province
          postalCode
          website
          telephone
        }
      }
    }
  }
`;

interface PropertyListingVariables {
  slug: string;
}

interface Office {
  id: string;
  name: string;
  addressLine1: string | undefined;
  city: string | undefined;
  province: string | undefined;
  postalCode: string | undefined;
  website: string | undefined;
  telephone: string | undefined;
}

interface TeamMember {
  id: string;
  slug: string;
  name: string;
  avatarThumb: string | undefined;
}

interface ListingAgent {
  id: string;
  name: string;
  website: string | undefined;
  position: string | undefined;
  telephone: string | undefined;
  teamMember: TeamMember;
  office: Office;
}

interface ListingImage {
  id: string;
  url: string | undefined;
}

interface PropertyListing {
  id: string;
  publicRemarks: string | undefined;
  listingId: string | undefined;
  bedroomsTotal: string | undefined;
  bathroomTotal: string | undefined;
  parking: string;
  parkingType: string | undefined;
  moreInformationLink: string | undefined;
  lease: string | undefined;
  leasePerUnit: string | undefined;
  leasePerTime: string | undefined;
  price: string | undefined;
  addressLine1: string | undefined;
  city: string | undefined;
  province: string | undefined;
  postalCode: string | undefined;
  unitNumber: string | undefined;
  propertyType: string | undefined;
  buildingType: string | undefined;
  storiesTotal: string | undefined;
  communityName: string | undefined;
  ownershipType: string | undefined;
  sizeTotalText: string | undefined;
  constructedDate: string | undefined;
  ammenitiesNearBy: string | undefined;
  communityFeatures: string | undefined;
  parkingSpaceTotal: string | undefined;
  roadType: string | undefined;
  basementDevelopment: string | undefined;
  basementType: string | undefined;
  halfBathTotal: string | undefined;
  coolingType: string | undefined;
  exteriorFinish: string | undefined;
  flooringType: string | undefined;
  heatingFuel: string | undefined;
  heatingType: string | undefined;
  constructionStyleAttachment: string | undefined;
  sewer: string | undefined;
  utilityWater: string | undefined;
  images: ListingImage[];
  listingAgent: ListingAgent;
}

interface PropertyListingData {
  propertyListing: PropertyListing;
}

const usePropertyListingQuery = (
  options?: QueryHookOptions<PropertyListingData, PropertyListingVariables>,
) => {
  return useQuery<PropertyListingData, PropertyListingVariables>(PROPERTY_LISTING_QUERY, options);
};

export { usePropertyListingQuery };
export type {
  PropertyListingData,
  ListingAgent,
  Office,
  PropertyListing,
  ListingImage,
  TeamMember,
};
